/**
 * SHOWICT Quantum — adapter de SOLO LECTURA sobre el motor legacy.
 *
 * Traduce la config Quantum a la config del motor (SOLO las 3 claves que el
 * motor conoce: pivotLen/atrMult/atrLen — los toggles visuales, budgets y la
 * capa de derivación nunca cruzan la frontera), llama a `detect` del motor y
 * mapea su salida al shape interno (types.js).
 *
 * CAPA NUEVA de derivación (no existe en el motor, documentada en
 * docs/indicators/showict_quantum.md §2): por cada señal no-fake deriva
 *   - direction (type U→LONG, D→SHORT) y grade (bos*→'A+', choch*→'A'; la
 *     etiqueta pintada conserva el texto original del motor: 'A+'/'A-'/'A');
 *   - entry = cierre de la vela de la señal;
 *   - SL tras el swing relevante: mínimo de los pivot-lows confirmados (misma
 *     condición de tamaño del motor: size ≥ atrMult×ATR del pivot) entre el
 *     pivot que originó el nivel roto y la vela de señal, menos slPadding×ATR
 *     de la señal (SHORT simétrico con pivot-highs, más padding). Fallback
 *     sin pivot válido: levelPrice ∓ atrMult×ATR. Se DESCARTA la señal si el
 *     SL queda del lado incorrecto del entry (riesgo ≤ 0);
 *   - TP1/TP2/TP3 = entry ± mult × |entry − sl| con los R-múltiplos de
 *     config (tpMults, defaults 1/2/3);
 *   - tracking read-only sobre velas posteriores (toque por high/low): SL→BE
 *     tras TP1 (semántica PRQ) y outcome active|win(TP3)|be(SL tras TP1)|
 *     loss(SL antes de TP1).
 *
 * Los fakes ('F') pasan a un array aparte: solo pintura, NUNCA budget.
 *
 * Nunca lanza excepción: cualquier entrada inválida devuelve la salida vacía.
 */

import { detect as engineDetect, defaultConfig as engineDefaultConfig, calcRawPivots, calcATR } from './engine.js'

// ── Helpers de tiempo (molde patron_ruptura_quantum/adapter.js:17-25) ──────

function toSeconds(t) {
  if (t == null || t === '' || Number.isNaN(t)) return null
  return t > 1e10 ? Math.floor(t / 1000) : t
}

function cleanTime(t) {
  if (t == null || t === '' || Number.isNaN(t)) return null
  return toSeconds(t)
}

function emptyOutput() {
  return {
    signals: [],
    fakes: [],
    pivotLabels: [],
    activeLevels: { activeH: null, activeL: null, bullBias: true },
    activeSignal: null,
    lastSignal: null,
    stats: null,
  }
}

/**
 * Traduce la config Quantum resuelta a la config del motor: solo las claves
 * que el motor conoce (los toggles visuales/Quantum y la capa de derivación
 * no cruzan la frontera).
 */
function toEngineConfig(cfg) {
  return {
    ...engineDefaultConfig,
    pivotLen: cfg.pivotLen,
    atrMult: cfg.atrMult,
    atrLen: cfg.atrLen,
  }
}

/**
 * SL de una señal (capa de derivación, ver cabecera). Devuelve null si el SL
 * queda del lado incorrecto del entry (la señal se descarta).
 *
 * LONG: mínimo de los pivot-lows confirmados (condición de tamaño del motor)
 * entre el pivot que originó el nivel roto (originIdx) y la vela de señal
 * (sigIdx), menos slPadding×ATR de la señal. Fallback: levelPrice −
 * atrMult×ATR. SHORT: simétrico con pivot-highs, más padding.
 */
function deriveSl(sig, direction, entry, candles, sigIdx, originIdx, rawPivots, atrs, cfg) {
  const { rawHighs, rawLows } = rawPivots
  const isLong = direction === 'LONG'
  const rawSide = isLong ? rawLows : rawHighs
  const from = Math.max(1, Number.isFinite(originIdx) ? originIdx : 0)
  const to = Math.min(sigIdx, candles.length - 2) // la condición de tamaño usa closes vecinos (j±1)

  let best = null
  for (let j = from; j <= to; j++) {
    const pivot = rawSide[j]
    if (pivot == null) continue
    // Misma condición de tamaño que el motor (engine.js líneas 75/86).
    const size = isLong
      ? Math.min(candles[j + 1].close, candles[j - 1].close) - pivot
      : pivot - Math.max(candles[j + 1].close, candles[j - 1].close)
    if (size >= cfg.atrMult * atrs[j]) {
      if (best === null || (isLong ? pivot < best : pivot > best)) best = pivot
    }
  }

  const atr = Number.isFinite(sig.atr) ? sig.atr : 0
  const base = best !== null
    ? best
    : (isLong ? sig.levelPrice - cfg.atrMult * atr : sig.levelPrice + cfg.atrMult * atr) // fallback
  if (!Number.isFinite(base)) return null
  const sl = isLong ? base - cfg.slPadding * atr : base + cfg.slPadding * atr
  // Salvaguarda: riesgo ≤ 0 (SL del lado incorrecto del entry) → señal descartada.
  if (isLong && sl >= entry) return null
  if (!isLong && sl <= entry) return null
  return sl
}

/**
 * Tracking read-only sobre velas posteriores a la señal (toque por high/low
 * de vela). SL→BE tras TP1 (semántica PRQ): el stop sube al entry al tocar
 * TP1; outcome 'win' = TP3 alcanzado, 'be' = SL tocado tras TP1, 'loss' = SL
 * antes de TP1. Dentro de una vela se evalúa primero el SL (conservador,
 * igual que el motor PRQ) y luego los TPs en orden.
 */
function trackSignal(out, candles, sigIdx) {
  const isLong = out.direction === 'LONG'
  let sl = out.sl

  for (let k = sigIdx + 1; k < candles.length; k++) {
    const c = candles[k]
    const slTouched = isLong ? c.low <= sl : c.high >= sl
    if (slTouched) {
      out.outcome = out.tp1Hit != null ? 'be' : 'loss'
      out.state = 'SL'
      out.closeTime = cleanTime(c.time)
      return out
    }
    if (out.tp1Hit == null && (isLong ? c.high >= out.tp1 : c.low <= out.tp1)) {
      out.tp1Hit = cleanTime(c.time)
      sl = out.entry // SL→BE tras TP1 (semántica PRQ)
    }
    if (out.tp2Hit == null && (isLong ? c.high >= out.tp2 : c.low <= out.tp2)) {
      out.tp2Hit = cleanTime(c.time)
    }
    if (out.tp3Hit == null && (isLong ? c.high >= out.tp3 : c.low <= out.tp3)) {
      out.tp3Hit = cleanTime(c.time)
      out.outcome = 'win'
      out.state = 'TP3'
      out.closeTime = cleanTime(c.time)
      return out
    }
  }

  out.state = out.tp2Hit != null ? 'TP2' : out.tp1Hit != null ? 'TP1' : 'OPEN'
  return out
}

/**
 * Señal del motor → ShowictSignal. Devuelve null si la señal se descarta
 * (vela no localizable, SL inválido). Los fakes NO pasan por aquí.
 */
function mapSignal(sig, candles, indexByTime, rawPivots, atrs, cfg, context) {
  if (!sig || (sig.type !== 'bosU' && sig.type !== 'chochU' && sig.type !== 'bosD' && sig.type !== 'chochD')) return null
  if (!Number.isFinite(sig.levelPrice)) return null
  const sigIdx = indexByTime.get(sig.time)
  if (sigIdx == null) return null
  const time = cleanTime(sig.time)
  if (time == null) return null

  const direction = sig.type === 'bosU' || sig.type === 'chochU' ? 'LONG' : 'SHORT'
  const grade = sig.type === 'bosU' || sig.type === 'bosD' ? 'A+' : 'A'
  const entry = candles[sigIdx].close
  if (!Number.isFinite(entry)) return null

  const originIdx = indexByTime.get(sig.levelTime) ?? null
  const sl = deriveSl(sig, direction, entry, candles, sigIdx, originIdx, rawPivots, atrs, cfg)
  if (sl === null) return null

  const risk = Math.abs(entry - sl)
  const isLong = direction === 'LONG'
  const tp1 = isLong ? entry + cfg.tpMults.tp1 * risk : entry - cfg.tpMults.tp1 * risk
  const tp2 = isLong ? entry + cfg.tpMults.tp2 * risk : entry - cfg.tpMults.tp2 * risk
  const tp3 = isLong ? entry + cfg.tpMults.tp3 * risk : entry - cfg.tpMults.tp3 * risk

  const symbol = typeof context?.symbol === 'string' && context.symbol ? context.symbol : 'na'
  const timeframe = typeof context?.timeframe === 'string' && context.timeframe ? context.timeframe : 'na'

  const out = {
    id: `showict:${symbol}:${timeframe}:${time}`,
    time,
    direction,
    grade,
    label: typeof sig.label === 'string' ? sig.label : grade,
    type: sig.type,
    levelPrice: sig.levelPrice,
    levelTime: cleanTime(sig.levelTime),
    atr: Number.isFinite(sig.atr) ? sig.atr : null,
    entry,
    sl,
    tp1,
    tp2,
    tp3,
    state: 'OPEN',
    outcome: 'active',
    tp1Hit: null,
    tp2Hit: null,
    tp3Hit: null,
    closeTime: null,
  }
  return trackSignal(out, candles, sigIdx)
}

/** Fake break del motor → ShowictFake (solo pintura, nunca budget). */
function mapFake(sig) {
  if (!sig || (sig.type !== 'fakeU' && sig.type !== 'fakeD')) return null
  const time = cleanTime(sig.time)
  if (time == null || !Number.isFinite(sig.levelPrice)) return null
  return { time, type: sig.type, levelPrice: sig.levelPrice, levelTime: cleanTime(sig.levelTime) }
}

// ── Invalidación por señal contraria (capa de derivación, v1.2) ─────────────
// Una señal contraria de grado A/A+ (BOS/CHoCH — los fakes nunca llegan a
// `signals`, van a `fakes`) INVALIDA todas las señales abiertas en dirección
// opuesta. "Abierta" en el momento del giro = outcome 'active' O cerrada
// DESPUÉS de la vela invalidante (su TP/SL posterior ya no cuenta: la
// estructura giró antes). Las de la misma dirección NO invalidan (pirámides
// conviven con tracking independiente). La invalidante ya lleva su propio
// tracking hecho (mapSignal), con normalidad.
function applyInvalidation(signals, candleByTime) {
  for (const opp of signals) {
    for (const s of signals) {
      if (s.direction === opp.direction) continue
      if (!(opp.time > s.time)) continue
      const wasOpenAt = s.outcome === 'active' || (s.closeTime != null && s.closeTime > opp.time)
      if (!wasOpenAt) continue
      const closePrice = candleByTime.get(opp.time)?.close
      if (!Number.isFinite(closePrice)) continue
      // Coherencia temporal: el progreso posterior al cierre se trunca.
      if (s.tp1Hit != null && s.tp1Hit > opp.time) s.tp1Hit = null
      if (s.tp2Hit != null && s.tp2Hit > opp.time) s.tp2Hit = null
      if (s.tp3Hit != null && s.tp3Hit > opp.time) s.tp3Hit = null
      const risk = Math.abs(s.entry - s.sl)
      s.outcome = 'invalidada'
      s.closeTime = opp.time
      s.closePrice = closePrice
      // R realizado: ±(closePrice − entrada)/riesgo con signo por dirección.
      s.realizedR = risk > 0
        ? (s.direction === 'LONG' ? (closePrice - s.entry) / risk : (s.entry - closePrice) / risk)
        : 0
      s.state = s.tp2Hit != null ? 'TP2' : s.tp1Hit != null ? 'TP1' : 'OPEN'
    }
  }
}

/**
 * Ejecuta el motor sobre las velas y mapea su salida al shape Quantum.
 * @param {Array} candles  velas ya saneadas (OHLC numéricos)
 * @param {Object} cfg     config ya resuelta por resolveConfig()
 * @param {{symbol?: string|null, timeframe?: string|null}} context
 */
export function runAdapter(candles, cfg, context) {
  try {
    if (!Array.isArray(candles) || candles.length === 0 || !cfg) return emptyOutput()
    const result = engineDetect(candles, toEngineConfig(cfg))
    if (!result || typeof result !== 'object') return emptyOutput()

    // Mapa time crudo de vela → índice (el motor referencia velas por su
    // time; el seguimiento de TP/SL necesita recorrer índices) y mapa de
    // time limpio → vela (closePrice de la invalidación).
    const indexByTime = new Map()
    const candleByTime = new Map()
    for (let i = 0; i < candles.length; i++) {
      if (!indexByTime.has(candles[i].time)) indexByTime.set(candles[i].time, i)
      const t = cleanTime(candles[i].time)
      if (t != null && !candleByTime.has(t)) candleByTime.set(t, candles[i])
    }

    // ATR y pivots crudos con los helpers del motor (misma semántica exacta):
    // la derivación de SL reutiliza la condición de tamaño del motor.
    const atrs = calcATR(candles, cfg.atrLen)
    const rawPivots = calcRawPivots(candles, cfg.pivotLen)

    const engineSignals = Array.isArray(result.signals) ? result.signals : []
    const signals = []
    const fakes = []
    for (const s of engineSignals) {
      if (s?.type === 'fakeU' || s?.type === 'fakeD') {
        const f = mapFake(s)
        if (f) fakes.push(f)
      } else {
        const mapped = mapSignal(s, candles, indexByTime, rawPivots, atrs, cfg, context)
        if (mapped) signals.push(mapped)
      }
    }

    // Invalidación por señal contraria (flag de config; false = comportamiento
    // ronda 2: las señales viven hasta su TP3/SL aunque la estructura gire).
    if (cfg.invalidateOnOppositeSignal !== false) applyInvalidation(signals, candleByTime)

    const pivotLabels = (Array.isArray(result.pivotLabels) ? result.pivotLabels : [])
      .map(p => {
        const time = cleanTime(p?.time)
        if (time == null || !Number.isFinite(p?.price) || typeof p?.label !== 'string') return null
        return { time, price: p.price, label: p.label, isHigh: p.isHigh === true }
      })
      .filter(Boolean)

    const activeLevels = {
      activeH: Number.isFinite(result.activeH) ? result.activeH : null,
      activeL: Number.isFinite(result.activeL) ? result.activeL : null,
      bullBias: result.bullBias === true,
    }

    const activeSignal = [...signals].reverse().find(s => s.outcome === 'active') ?? null
    const lastSignal = signals.length > 0 ? signals[signals.length - 1] : null

    return { signals, fakes, pivotLabels, activeLevels, activeSignal, lastSignal, stats: null }
  } catch {
    return emptyOutput()
  }
}
